import React from "react";
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
    useRoutes,
} from "react-router-dom";
import { wrapUseRoutes } from "@sentry/react";

import * as Sentry from "@sentry/react";

// Flag to check if Sentry is initialized
let isSentryInitialized = false;

// Auth-related error patterns that are not actionable bugs — filter
// them out to avoid wasting Sentry quota on expected 401 flows.
const AUTH_ERROR_PATTERNS = [
    'token is invalid or expired',
    'token is blacklisted',
    'given token not valid',
    'authentication credentials were not provided',
    'session has timed out',
    'token_not_valid',
];

const matchesAuthPattern = (msg) => AUTH_ERROR_PATTERNS.some(p => msg.includes(p));

const isAuthError = (event, hint) => {
    const original = hint?.originalException;
    if (original) {
        const msg = (typeof original === 'string' ? original : original?.message || '').toLowerCase();
        if (matchesAuthPattern(msg)) return true;
    }
    const values = event?.exception?.values || [];
    return values.some(v => matchesAuthPattern((v?.value || '').toLowerCase()));
};

// Initialize Sentry for production AND pre-prod. Staging is excluded to avoid
// burning quota on heavy test traffic. Environment tag lets you filter in the
// Sentry dashboard. Previously only production was captured — pre-prod bugs
// were invisible until they hit real users.
const SENTRY_ENVIRONMENTS = {
    'https://dental-sem.co.uk': 'production',
    'https://pre-prod.dental-sem.co.uk': 'pre-production',
};
const sentryEnvironment = SENTRY_ENVIRONMENTS[window.location.origin];

if (sentryEnvironment) {
    Sentry.init({
        dsn: "https://6e2eb0942fb174e880057e2406184025@o1361470.ingest.us.sentry.io/4508273023582208",
        environment: sentryEnvironment,
        sendDefaultPii: false,
        ignoreErrors: [
            // Browser extensions (Grammarly, etc.) and React DOM race conditions
            "Failed to execute 'removeChild' on 'Node'",
            "Failed to execute 'insertBefore' on 'Node'",
            // Stale chunk errors after deploys — handled by lazyRetry reload
            'Loading chunk',
            'Loading CSS chunk',
            'Importing a module script failed',
            // ResizeObserver noise from MUI/browser
            'ResizeObserver loop',
        ],
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                // Mask all text in replays to protect patient PII (GDPR)
                maskAllText: true,
                maskTextSelector: null,
                blockAllMedia: false,

                // Capture network metadata only — no request/response bodies (GDPR)
                networkDetailAllowUrls: [/^https?:\/\//],
                networkCaptureBodies: false,
                // Capture a broad set of headers; some may still be unavailable due to browser/CORS
                networkRequestHeaders: [
                    'Authorization',
                    'Content-Type',
                    'Accept',
                    'Accept-Language',
                    'Cache-Control',
                    'Pragma',
                    'Origin',
                    'Referer'
                ],
                networkResponseHeaders: [
                    'Content-Type',
                    'Content-Length',
                    'Cache-Control',
                    'Date',
                    'Server',
                ],
            }),
        ],
        // Filter out noisy auth errors before they consume Sentry quota
        beforeSend(event, hint) {
            if (isAuthError(event, hint)) return null;
            return event;
        },
        // Sample 20% of transactions — 100% was excessive and costly
        tracesSampleRate: 0.2,
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [/^\//, /^https:\/\/api\.dental-sem\.co\.uk/],

        // Capture Replay for 10% of all sessions,
        // plus 100% of sessions with an error (restored from 50% — error volume
        // is now very low after the Sentry fix sprint, so cost is negligible).
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    });
    isSentryInitialized = true;
}

// Use Sentry routes wrapper only if Sentry is initialized
export const useSentryRoutes = isSentryInitialized
    ? wrapUseRoutes(useRoutes)
    : useRoutes;
